import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { EmptyState } from "../components/EmptyState";
import { PageSkeleton } from "../components/LoadingSkeleton";
import { SeverityBadge } from "../components/SeverityBadge";
import { IconShield } from "../components/icons";
import { fmtDateTime } from "../lib/format";
import type { DataQualityDashboardDto } from "@bi/types";

export function DataQualityPage() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["data-quality", "dashboard"],
    queryFn: () => api.get<DataQualityDashboardDto>("/data-quality/dashboard"),
    staleTime: 120_000,
  });

  if (isLoading) return <PageSkeleton />;

  if (isError || !data) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Qualidade dos Dados"
          subtitle="Monitoramento de integridade, consistência e completude dos dados integrados"
        />
        <EmptyState
          icon={<IconShield className="w-7 h-7" />}
          title="Sem dados de qualidade disponíveis"
          description="Não foi possível carregar o painel de qualidade. As verificações automáticas rodam diariamente às 02h."
        />
      </div>
    );
  }

  const d = data;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Qualidade dos Dados"
        subtitle="Monitoramento de integridade, consistência e completude dos dados integrados"
      />

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="card p-4">
          <p className="text-2xl font-bold text-danger-600">{d.summary.open}</p>
          <p className="text-sm text-gray-500 mt-0.5">Problemas abertos</p>
        </div>
        <div className="card p-4">
          <p className="text-2xl font-bold text-tm-700">{d.summary.resolved}</p>
          <p className="text-sm text-gray-500 mt-0.5">Resolvidos</p>
        </div>
        <div className="card p-4">
          <p className="text-2xl font-bold text-gray-400">{d.summary.ignored}</p>
          <p className="text-sm text-gray-500 mt-0.5">Ignorados</p>
        </div>
      </div>

      {/* By entity */}
      {d.byEntity.length > 0 && (
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-border">
            <p className="text-sm font-semibold text-gray-900">Problemas por Entidade</p>
          </div>
          <div className="divide-y divide-surface-border">
            {d.byEntity.map((e, i) => (
              <div key={i} className="px-5 py-3 flex items-center gap-4">
                <p className="text-sm font-medium text-gray-800 w-36 flex-shrink-0">{e.entity}</p>
                <div className="flex items-center gap-3 flex-1">
                  {e.critical > 0 && <span className="badge-red">{e.critical} críticos</span>}
                  {e.warning > 0 && <span className="badge-amber">{e.warning} atenção</span>}
                </div>
                <p className="text-sm font-semibold text-gray-900">{e.open} abertos</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent issues table */}
      <div className="card overflow-hidden">
        <div className="px-5 py-4 border-b border-surface-border">
          <p className="text-sm font-semibold text-gray-900">Problemas Recentes</p>
        </div>
        {d.recentIssues.length === 0 ? (
          <EmptyState
            icon={<IconShield className="w-7 h-7" />}
            title="Nenhum problema encontrado"
            description="Os dados estão com boa qualidade. Verificações automáticas rodam a cada 24 horas."
            compact
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface-muted border-b border-surface-border">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Entidade</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Tipo</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Severidade</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Descrição</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Detectado</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border">
                {d.recentIssues.map((issue) => (
                  <tr key={issue.id} className="hover:bg-surface-muted/50">
                    <td className="px-5 py-3 font-medium text-gray-800">{issue.entity}</td>
                    <td className="px-5 py-3 text-gray-600">{issue.issueType}</td>
                    <td className="px-5 py-3"><SeverityBadge severity={issue.severity} /></td>
                    <td className="px-5 py-3 text-gray-600 max-w-xs truncate">{issue.description}</td>
                    <td className="px-5 py-3 text-gray-400">{fmtDateTime(issue.detectedAt)}</td>
                    <td className="px-5 py-3">
                      <span className={issue.status === "open" ? "badge-red" : issue.status === "resolved" ? "badge-green" : "badge-gray"}>
                        {issue.status === "open" ? "Aberto" : issue.status === "resolved" ? "Resolvido" : "Ignorado"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

